import React, { ReactElement, createContext, useContext, useEffect, useState } from 'react';

import { Voucher, VoucherStatus } from '../../../api/types/voucher';
import { useGetVouchers } from '../../../hooks/queries/useVoucherQuery';

export enum VoucherFilterType {
  STATUS = 'Status',
}

export type VoucherContextType = {
  vouchers: Voucher[];
  setVouchers: (vouchers: Voucher[]) => void;
  filteredVouchers: Voucher[];
  setFilteredVouchers: (filteredVouchers: Voucher[]) => void;
  fetchVoucherIsLoading: boolean;
  fetchVoucherIsError: boolean;
  filters: {
    selectedFilters: VoucherFilterType[];
    setSelectedFilters: (selectedFilters: VoucherFilterType[]) => void;
    selectedStatus: string[];
    setSelectedStatus: (selectedStatus: string[]) => void;
    statusOptions: string[];
    setStatusOptions: (statusOptions: string[]) => void;
  };
};

export const VoucherContext = createContext<VoucherContextType | null>(undefined);

export const VoucherContextProvider = ({ children }: { children: ReactElement }) => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [filteredVouchers, setFilteredVouchers] = useState<Voucher[]>([]);
  const [fetchVoucherIsLoading, setFetchVoucherIsLoading] = useState(true);
  const [fetchVoucherIsError, setFetchVoucherIsError] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState<VoucherFilterType[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);

  useEffect(() => {
    setStatusOptions(VoucherStatus.options);
  }, []);

  const { data: fetchVouchers, isLoading: fetchVouchersIsLoading, isError: fetchVouchersIsError } = useGetVouchers();

  useEffect(() => {
    if (fetchVouchersIsLoading) setFetchVoucherIsLoading(true);
    else setFetchVoucherIsLoading(false);

    if (fetchVouchersIsError) setFetchVoucherIsError(true);
    else setFetchVoucherIsError(false);

    const sortedVouchers: Voucher[] =
      fetchVouchers && fetchVouchers.length > 0
        ? [
            ...fetchVouchers
              .filter((voucher) => voucher.status === 'Active')
              .sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime()),
            ...fetchVouchers
              .filter((voucher) => voucher.status === 'Used')
              .sort((a, b) => new Date(a.usedDate).getTime() - new Date(b.usedDate).getTime()),
            ...fetchVouchers
              .filter((voucher) => voucher.status === 'Expired')
              .sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime()),
          ]
        : [];

    setVouchers(sortedVouchers);
    setFilteredVouchers(sortedVouchers);
  }, [fetchVouchers, fetchVouchersIsLoading, fetchVouchersIsError]);

  // Filter sessions based on the selected filters
  useEffect(() => {
    let filteredVouchers = [...vouchers];

    if (selectedFilters.includes(VoucherFilterType.STATUS)) {
      filteredVouchers =
        selectedStatus.length > 0 ? filteredVouchers.filter((voucher) => selectedStatus.includes(voucher.status)) : [];
    }
    setFilteredVouchers(filteredVouchers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, selectedStatus]);

  return (
    <VoucherContext.Provider
      value={{
        vouchers,
        setVouchers,
        filteredVouchers,
        setFilteredVouchers,
        fetchVoucherIsLoading,
        fetchVoucherIsError,
        filters: {
          selectedFilters,
          setSelectedFilters,
          selectedStatus,
          setSelectedStatus,
          statusOptions,
          setStatusOptions,
        },
      }}
    >
      {children}
    </VoucherContext.Provider>
  );
};

export const withVoucherContextProvider = (Component: React.ComponentType<any>) => {
  const WrappedComponent = (props: any) => (
    <VoucherContextProvider>
      <Component {...props} />
    </VoucherContextProvider>
  );
  return WrappedComponent;
};

export default function useVoucherContext() {
  const context = useContext(VoucherContext);

  return context;
}
